import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Eye, EyeOff, LogIn, ShieldCheck, Lock, ArrowLeft, MailCheck } from 'lucide-react'
import { AuthService, type OtpChallenge } from '@/services/auth/AuthService'
import { useAuthStore } from '@/stores/authStore'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { OtpInput } from '@/components/ui/OtpInput'
import { toast } from '@/components/ui/Toast'

const schema = z.object({
  username: z.string().min(1, 'Informe o usuário.'),
  password: z.string().min(1, 'Informe a senha.'),
})
type FormValues = z.infer<typeof schema>

const APP_VERSION = '3.0'

/** Extrai a melhor mensagem de erro de uma resposta da API. */
function apiMessage(e: unknown): string | null {
  const data = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data
  const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined
  return firstError ?? data?.message ?? null
}

export function LoginPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [step, setStep] = useState<'credentials' | 'otp'>('credentials')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPwd, setShowPwd] = useState(false)

  // --- Estado da etapa OTP ---
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null)
  const [code, setCode] = useState('')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendIn, setResendIn] = useState(0)

  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: { username: '', password: '' },
  })

  // Contagem regressiva para liberar o reenvio.
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [resendIn])

  async function onSubmitCredentials(values: FormValues) {
    const parsed = schema.safeParse(values)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const result = await AuthService.signIn(values.username, values.password)
      if (result.kind === 'authenticated') {
        const { accessToken, warning, ...user } = result.user
        setAuth(user, accessToken)
        if (warning) {
          toast.warning({ title: 'Verificação em duas etapas desativada', description: warning, duration: 9000 })
        }
        navigate('/', { replace: true })
        return
      }
      // Exige verificação em duas etapas → vai para a tela do código.
      setChallenge(result.challenge)
      setCode('')
      setOtpError(null)
      setInfo(null)
      setResendIn(result.challenge.resendInSeconds ?? 60)
      setStep('otp')
    } catch (e: unknown) {
      setError(apiMessage(e) ?? 'Falha ao entrar. Verifique suas credenciais.')
    } finally {
      setSubmitting(false)
    }
  }

  async function verify(theCode: string) {
    if (!challenge || theCode.length < 6 || verifying) return
    setVerifying(true)
    setOtpError(null)
    try {
      const payload = await AuthService.verifyOtp(challenge.challengeId, theCode)
      const { accessToken, ...user } = payload
      setAuth(user, accessToken)
      navigate('/', { replace: true })
    } catch (e: unknown) {
      setOtpError(apiMessage(e) ?? 'Não foi possível validar o código.')
      setCode('')
    } finally {
      setVerifying(false)
    }
  }

  async function resend() {
    if (!challenge || resendIn > 0 || resending) return
    setResending(true)
    setOtpError(null)
    setInfo(null)
    try {
      const r = await AuthService.resendOtp(challenge.challengeId)
      setResendIn(r.resendInSeconds ?? 60)
      setCode('')
      setInfo('Enviamos um novo código para o seu e-mail.')
    } catch (e: unknown) {
      setOtpError(apiMessage(e) ?? 'Não foi possível reenviar o código.')
    } finally {
      setResending(false)
    }
  }

  function backToCredentials() {
    setStep('credentials')
    setChallenge(null)
    setCode('')
    setOtpError(null)
    setInfo(null)
    setError(null)
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 lg:grid lg:grid-cols-2">
      {/* Painel da marca (desktop) */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-12 text-white">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-brand-400/20 blur-3xl" />

        <div className="relative z-10">
          <img src="/logo-light-full.png" alt="Ampla+ Benefícios" className="h-16 w-auto object-contain brightness-0 invert" />
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="text-3xl font-semibold leading-tight">
            Gestão completa do seu plano de benefícios
          </h2>
          <p className="mt-4 text-brand-100/90">
            Associados, empresas, credenciados, faturamento e comissionamento — tudo em um só lugar.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-brand-50">
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15"><ShieldCheck size={16} /></span>
              Acesso seguro com verificação em duas etapas
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15"><Lock size={16} /></span>
              Seus dados protegidos de ponta a ponta
            </li>
          </ul>
        </div>

        <div className="relative z-10 text-xs text-brand-200/80">
          © {new Date().getFullYear()} Ampla Mais Benefícios · Versão {APP_VERSION}
        </div>
      </aside>

      {/* Conteúdo */}
      <main className="flex min-h-screen items-center justify-center px-4 py-10 lg:min-h-0">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <img src="/logo-light-full.png" alt="Ampla+ Benefícios" className="lg:hidden mx-auto mb-5 h-12 w-auto object-contain" />
            {step === 'credentials' ? (
              <>
                <h1 className="text-2xl font-semibold text-gray-900">Bem-vindo de volta</h1>
                <p className="mt-1.5 text-sm text-gray-500">Entre com suas credenciais para continuar</p>
              </>
            ) : (
              <>
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                  <MailCheck size={22} />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900">Verificação em duas etapas</h1>
                <p className="mt-1.5 text-sm text-gray-500">
                  Enviamos um código de 6 dígitos para{' '}
                  <strong className="text-gray-700">{challenge?.destination}</strong>. Informe-o abaixo para continuar.
                </p>
              </>
            )}
          </div>

          {step === 'credentials' ? (
            <form onSubmit={handleSubmit(onSubmitCredentials)} className="space-y-4">
              <Input label="Usuário" placeholder="seu.usuario" {...register('username')} />
              <Input
                label="Senha"
                type={showPwd ? 'text' : 'password'}
                placeholder="••••••••"
                iconRight={
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                          className="pointer-events-auto cursor-pointer text-gray-400 hover:text-gray-600" tabIndex={-1}
                          aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                }
                {...register('password')}
              />

              {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
              )}

              <Button type="submit" loading={submitting} iconLeft={<LogIn size={15} />} className="w-full" size="lg">
                {submitting ? 'Entrando' : 'Entrar'}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={verify}
                disabled={verifying}
                autoFocus
              />

              {otpError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-center text-sm text-rose-700">{otpError}</div>
              )}
              {info && !otpError && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-sm text-emerald-700">{info}</div>
              )}

              <Button
                type="button"
                onClick={() => verify(code)}
                loading={verifying}
                disabled={code.length < 6}
                iconLeft={<ShieldCheck size={15} />}
                className="w-full"
                size="lg"
              >
                {verifying ? 'Validando' : 'Validar e entrar'}
              </Button>

              <div className="text-center text-sm text-gray-500">
                Não recebeu?{' '}
                {resendIn > 0 ? (
                  <span className="text-gray-400">Reenviar em {resendIn}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={resend}
                    disabled={resending}
                    className="cursor-pointer font-medium text-brand-600 hover:text-brand-700 disabled:opacity-60"
                  >
                    {resending ? 'Reenviando…' : 'Reenviar código'}
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={backToCredentials}
                className="mx-auto flex cursor-pointer items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft size={14} /> Voltar ao login
              </button>
            </div>
          )}

          <p className="mt-10 text-center text-[11px] text-gray-400">
            Ampla Mais Benefícios · Versão {APP_VERSION}
          </p>
        </div>
      </main>
    </div>
  )
}

export default LoginPage
